
import React, { useState, useEffect, useCallback } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Comments from '../components/Comments';
import '../css.css';

const HEADERS = [
  { key: 'name', label: '이름' },
  { key: 'type', label: '종류' },
  { key: 'weight_max', label: '최대하중(kg)' },
  { key: 'diameter', label: '지름(mm)' },
  { key: 'length', label: '길이' },
  { key: 'price', label: '가격(실버)' },
  { key: 'color', label: '색상' },
];

const Page1 = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, text: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await fetch('/api/line');
        if (!res.ok) throw new Error('데이터를 불러올 수 없습니다');
        const rows = await res.json();
        setData(rows);
        setFilteredData(rows);
      } catch (e) {
        setData([]);
        setFilteredData([]);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  // 검색 기능
  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
    if (!term.trim()) {
      setFilteredData(data);
      return;
    }

    const filtered = data.filter(item => {
      const searchLower = term.toLowerCase();
      
      return (
        (item.name && item.name.toLowerCase().includes(searchLower)) ||
        (item.type && item.type.toLowerCase().includes(searchLower)) ||
        (item.color && item.color.toLowerCase().includes(searchLower)) ||
        (item.length && item.length.toString().toLowerCase().includes(searchLower)) ||
        (item.weight_max && item.weight_max.toString().toLowerCase().includes(searchLower)) ||
        (item.diameter && item.diameter.toString().toLowerCase().includes(searchLower)) ||
        (item.price && item.price.toString().toLowerCase().includes(searchLower))
      );
    });
    
    setFilteredData(filtered);
  }, [data]);

  useEffect(() => {
    handleSearch(searchTerm);
  }, [data, searchTerm, handleSearch]);

  // 정렬 함수
  const handleSort = (key) => {
    // 이름 컬럼을 클릭하면 기본 정렬로 리셋
    if (key === 'name') {
      setSortConfig({ key: null, direction: 'asc' });
      return;
    }

    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // 정렬된 데이터
  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // 숫자형 컬럼 처리 (최대하중, 지름, 가격)
      if (['weight_max', 'diameter', 'price'].includes(sortConfig.key)) {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
        
        if (sortConfig.direction === 'asc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      }

      // 문자열 컬럼 처리 (이름, 종류, 길이, 색상)
      aValue = String(aValue || '');
      bValue = String(bValue || '');
      
      if (sortConfig.direction === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  }, [filteredData, sortConfig]);

  // 정렬 아이콘
  const getSortIcon = (columnKey) => {
    // 이름 컬럼은 기본 정렬 버튼
    if (columnKey === 'name') {
      return sortConfig.key === null ? '↕️' : '🔄';
    }

    if (sortConfig.key !== columnKey) {
      return '↕️';
    }
    return sortConfig.direction === 'asc' ? 
      <span style={{ color: 'green' }}>▲</span> : 
      <span style={{ color: 'red' }}>▼</span>;
  };

  // 툴팁 핸들러
  const handleMouseEnter = (e, isPromotion) => {
    if (isPromotion) {
      setTooltip({
        show: true,
        x: e.clientX,
        y: e.clientY,
        text: '포모르 상점 전용'
      });
    }
  };

  const handleMouseMove = (e, isPromotion) => {
    if (isPromotion && tooltip.show) {
      setTooltip(prev => ({
        ...prev,
        x: e.clientX,
        y: e.clientY
      }));
    }
  };

  const handleMouseLeave = () => {
    setTooltip({ show: false, x: 0, y: 0, text: '' });
  };

  return (
    <>
      <h1 className="page-title">낚싯줄 정보</h1>
      <div className="page-info">
        <h4>색인 (🔘:투명/🟢:녹색/⚪️:흰색/🔵:파란색/🟠:주황색/🟣:보라색/🟡:노란색/⚫️:검은색/🔴:빨간색/🌈:무지개색)</h4>
      </div>
      <div>
        {loading ? (
          <div className="spinner-container">
            <LoadingSpinner height={80} width={80} color="#4fa94d" visible={true} />
          </div>
        ) : (
          <div className="page-content">
            {/* 검색 컨테이너 */}
            <div 
              className="search-container"
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '20px 0',
                width: '100%',
                maxWidth: '500px',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}
            >
              <input
                type="text"
                placeholder="낚싯줄 이름/스펙으로 검색"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  padding: '12px 16px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'border-color 0.3s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
              />
            </div>
            
            <table className="oil-table">
              <thead>
                <tr>
                  {HEADERS.map((h) => (
                    <th 
                      key={h.key}
                      onClick={() => handleSort(h.key)}
                      style={{ cursor: 'pointer' }}
                    >
                      {h.label} {getSortIcon(h.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row, idx) => {
                  const isPromotion = row.is_pormor === 1;
                  return (
                    <tr 
                      key={row.id || idx}
                      className={isPromotion ? 'promotion-item' : ''}
                      onMouseEnter={(e) => handleMouseEnter(e, isPromotion)}
                      onMouseMove={(e) => handleMouseMove(e, isPromotion)}
                      onMouseLeave={handleMouseLeave}
                    >
                      {HEADERS.map((h) => (
                        <td key={h.key}>
                          {row[h.key]}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        
        {/* 커스텀 툴팁 */}
        {tooltip.show && (
          <div 
            className="custom-tooltip"
            style={{
              position: 'fixed',
              left: tooltip.x + 10,
              top: tooltip.y - 30,
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 'normal',
              whiteSpace: 'nowrap',
              zIndex: 1000,
              pointerEvents: 'none',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
            }}
          >
            {tooltip.text}
          </div>
        )}

        <Comments pageName="line" />
      </div>
    </>
  );
};

export default Page1;